import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllClients } from '../utils/apiService';
import bastionAuth from '../services/bastionAuthService';

const UpdatePlanning = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('12:00-13:00');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('production');
  const [jiraTicket, setJiraTicket] = useState('');
  const [updateDescription, setUpdateDescription] = useState('');
  const [globalVersion, setGlobalVersion] = useState('');
  const [selectedComponents, setSelectedComponents] = useState({
    patientApp: false,
    managerFrontApp: false,
    marshaller: false,
    marshallerInterop: false,
    apache: false,
    mariadb: false,
    nodejs: false
  });
  const [targetVersions, setTargetVersions] = useState({
    patientApp: '',
    managerFrontApp: '',
    marshaller: '',
    marshallerInterop: '',
    apache: '',
    mariadb: '',
    nodejs: ''
  });
  const [scheduledUpdates, setScheduledUpdates] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [formVisible, setFormVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isCreatingJiraTicket, setIsCreatingJiraTicket] = useState(false);
  const [jiraCreationResult, setJiraCreationResult] = useState(null);
  const [jiraIssueTypes, setJiraIssueTypes] = useState([]);
  const [selectedIssueType, setSelectedIssueType] = useState('10001');
  const [jiraOrganizations, setJiraOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('4');
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  const [updateStatusFilter, setUpdateStatusFilter] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      console.log('🚀 Début du chargement des données...');
      
      // Charger les clients
      const clientsData = await getAllClients();
      setClients(clientsData);
      console.log('🎯 Clients chargés:', clientsData.length);
      
      // Charger les mises à jour planifiées depuis la base de données
      try {
        const response = await fetch('/api/scheduled-updates', {
          headers: bastionAuth.getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          // Convertir les dates string en objets Date
          const updatesWithDates = data.map(update => ({
            ...update,
            date: new Date(update.date)
          }));
          setScheduledUpdates(updatesWithDates);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des mises à jour planifiées:', error);
      }
      
      // Charger les types de tickets JIRA
      try {
        const response = await fetch('/api/jira/issue-types', {
          headers: bastionAuth.getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          setJiraIssueTypes(data.issueTypes || []);
          
          const majType = data.issueTypes.find(type => 
            type.name.toUpperCase() === 'MAJ' || 
            type.name.toLowerCase().includes('maj') ||
            type.name.toLowerCase().includes('mise à jour')
          );
          
          if (majType) {
            setSelectedIssueType(majType.id);
          } else if (data.issueTypes.length > 0) {
            setSelectedIssueType(data.issueTypes[0].id);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des types de tickets JIRA:', error);
      }
      
      // Charger les organisations JIRA
      try {
        const orgResponse = await fetch('/api/jira/organizations', {
          headers: bastionAuth.getAuthHeaders()
        });
        
        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          
          if (orgData.organizations && Array.isArray(orgData.organizations)) {
            setJiraOrganizations(orgData.organizations);
            
            const hoppenOrg = orgData.organizations.find(org => org.id === '4' || org.name === 'Hoppen');
            if (hoppenOrg) {
              setSelectedOrganization(hoppenOrg.id);
            } else if (orgData.organizations.length > 0) {
              setSelectedOrganization(orgData.organizations[0].id);
            }
          }
          setOrganizationsLoaded(true);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des organisations JIRA:', error);
        const defaultOrg = { id: '4', name: 'Hoppen' };
        setJiraOrganizations([defaultOrg]);
        setSelectedOrganization('4');
        setOrganizationsLoaded(true);
      }
      
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Fonction pour appliquer une version globale
  const applyGlobalVersion = () => {
    if (globalVersion) {
      const newVersions = {};
      Object.keys(targetVersions).forEach(component => {
        if (selectedComponents[component]) {
          newVersions[component] = globalVersion;
        } else {
          newVersions[component] = targetVersions[component];
        }
      });
      setTargetVersions(newVersions);
    }
  };

  // Fonction pour créer un ticket JIRA
  const createJiraTicket = async () => {
    if (!selectedClient) {
      setFormErrors(prev => ({
        ...prev,
        client: 'Veuillez sélectionner un client pour créer un ticket JIRA'
      }));
      return;
    }

    if (!selectedOrganization) {
      setFormErrors(prev => ({
        ...prev,
        organization: 'Veuillez sélectionner une organisation JIRA'
      }));
      return;
    }

    setIsCreatingJiraTicket(true);
    setJiraCreationResult(null);

    const selectedClientObj = clients.find(client => String(client.id) === String(selectedClient));

    if (!selectedClientObj) {
      setFormErrors(prev => ({
        ...prev,
        client: 'Client non trouvé'
      }));
      setIsCreatingJiraTicket(false);
      return;
    }

    const componentNames = {
      patientApp: 'Patient App',
      managerFrontApp: 'Manager Front App',
      marshaller: 'Marshaller',
      marshallerInterop: 'Marshaller Interop',
      apache: 'Apache',
      mariadb: 'MariaDB',
      nodejs: 'Node.js'
    };
    
    const componentsToUpdate = Object.entries(selectedComponents)
      .filter(([_, selected]) => selected)
      .map(([component]) => ({
        name: componentNames[component] || component,
        version: targetVersions[component] || 'Non spécifiée'
      }));

    const summary = `MAJ ${selectedClientObj.name} - ${selectedEnvironment.toUpperCase()} - ${selectedDate.toLocaleDateString('fr-FR')}`;
    
    const description = `Mise à jour planifiée pour ${selectedClientObj.name}\n\n` +
      `🏢 Client: ${selectedClientObj.name}\n` +
      `🌐 Environnement: ${selectedEnvironment.toUpperCase()}\n` +
      `📅 Date: ${selectedDate.toLocaleDateString('fr-FR')}\n` +
      `🕐 Créneau horaire: ${selectedTimeSlot}\n\n` +
      `📦 Composants à mettre à jour:\n${componentsToUpdate.map(c => `- ${c.name} → Version: ${c.version}`).join('\n')}\n\n` +
      `📝 Description: ${updateDescription || 'Mise à jour standard'}\n\n` +
      `--- Ticket créé automatiquement depuis Parc Info ---`;

    try {
      const response = await fetch('/api/jira/create-ticket', {
        method: 'POST',
        headers: bastionAuth.getAuthHeaders(),
        body: JSON.stringify({
          summary,
          description,
          issueType: selectedIssueType,
          components: [],
          date: selectedDate.toISOString(),
          timeSlot: selectedTimeSlot,
          clientName: selectedClientObj.name,
          targetVersions: targetVersions,
          organizationId: selectedOrganization
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const ticketKey = data.majTicket ? data.majTicket.key : data.key;
        setJiraTicket(ticketKey);
        
        let successMessage = `Ticket MAJ créé : ${ticketKey}`;
        if (data.buildTicket) {
          successMessage += ` | Ticket BUILD : ${data.buildTicket.key}`;
        }
        
        setJiraCreationResult({
          success: true,
          key: ticketKey,
          url: data.majTicket ? data.majTicket.url : data.url,
          message: successMessage,
          buildKey: data.buildTicket ? data.buildTicket.key : null
        });
      } else {
        setJiraCreationResult({
          success: false,
          error: data.error || 'Erreur lors de la création du ticket'
        });
      }
    } catch (error) {
      console.error('Erreur lors de la création du ticket JIRA:', error);
      setJiraCreationResult({
        success: false,
        error: 'Erreur de connexion au serveur'
      });
    } finally {
      setIsCreatingJiraTicket(false);
    }
  };

  // Fonction pour afficher le formulaire
  const showForm = (date = new Date()) => {
    setSelectedDate(date);
    setIsEditing(false);
    setEditingId(null);
    
    // Réinitialiser le formulaire
    setSelectedClient('');
    setSelectedEnvironment('production');
    setJiraTicket('');
    setUpdateDescription('');
    setGlobalVersion('');
    setSelectedComponents({
      patientApp: false,
      managerFrontApp: false,
      marshaller: false,
      marshallerInterop: false,
      apache: false,
      mariadb: false,
      nodejs: false
    });
    setTargetVersions({
      patientApp: '',
      managerFrontApp: '',
      marshaller: '',
      marshallerInterop: '',
      apache: '',
      mariadb: '',
      nodejs: ''
    });
    setJiraCreationResult(null);
    
    if (!selectedOrganization && jiraOrganizations.length > 0) {
      const hoppenOrg = jiraOrganizations.find(org => org.id === '4' || org.name === 'Hoppen');
      if (hoppenOrg) {
        setSelectedOrganization(hoppenOrg.id);
      } else {
        setSelectedOrganization(jiraOrganizations[0].id);
      }
    }
    
    setFormVisible(true);
    validateTimeSlot(date, selectedTimeSlot);
  };
  
  // Fonction pour éditer une mise à jour existante
  const editUpdate = (updateId) => {
    const updateToEdit = scheduledUpdates.find(update => update.id === updateId);
    
    if (updateToEdit) {
      setIsEditing(true);
      setEditingId(updateId);
      setSelectedDate(new Date(updateToEdit.date));
      setSelectedClient(updateToEdit.clientId);
      setSelectedEnvironment(updateToEdit.environment || 'production');
      setSelectedTimeSlot(updateToEdit.timeSlot);
      setJiraTicket(updateToEdit.jiraTicket);
      setUpdateDescription(updateToEdit.description || '');
      setSelectedComponents(updateToEdit.components || {
        patientApp: false,
        managerFrontApp: false,
        marshaller: false,
        marshallerInterop: false,
        apache: false,
        mariadb: false,
        nodejs: false
      });
      setTargetVersions(updateToEdit.targetVersions || {
        patientApp: '',
        managerFrontApp: '',
        marshaller: '',
        marshallerInterop: '',
        apache: '',
        mariadb: '',
        nodejs: ''
      });
      setJiraCreationResult(null);
      
      setFormVisible(true);
    }
  };
  
  // Fonction pour changer le statut d'une mise à jour
  const changeUpdateStatus = async (updateId, newStatus) => {
    try {
      const response = await fetch(`/api/scheduled-updates/${updateId}/status`, {
        method: 'PUT',
        headers: bastionAuth.getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updatedData = await response.json();
        setScheduledUpdates(prev => prev.map(update => {
          if (update.id === updateId) {
            return { ...update, status: newStatus };
          }
          return update;
        }));
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    }
  };

  // Fonction pour supprimer une mise à jour
  const deleteUpdate = async (updateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette mise à jour ?')) {
      return;
    }

    try {
      const response = await fetch(`/api/scheduled-updates/${updateId}`, {
        method: 'DELETE',
        headers: bastionAuth.getAuthHeaders()
      });

      if (response.ok) {
        setScheduledUpdates(prev => prev.filter(update => update.id !== updateId));
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  // Validation du créneau horaire
  const validateTimeSlot = (date, timeSlot) => {
    if (isEditing) {
      const editedUpdate = scheduledUpdates.find(update => update.id === editingId);
      if (editedUpdate && isSameDay(new Date(editedUpdate.date), date) && editedUpdate.timeSlot === timeSlot) {
        setFormErrors(prev => ({ ...prev, timeSlot: null }));
        return;
      }
    }
    
    const isTimeSlotTaken = scheduledUpdates.some(update => 
      isSameDay(new Date(update.date), date) && update.timeSlot === timeSlot && 
      (editingId === null || update.id !== editingId)
    );
    
    if (isTimeSlotTaken) {
      setFormErrors(prev => ({
        ...prev,
        timeSlot: 'Ce créneau horaire est déjà pris pour cette date'
      }));
    } else {
      setFormErrors(prev => ({
        ...prev,
        timeSlot: null
      }));
    }
  };

  // Validation de la limite de production
  const validateProdLimit = (date, isProd) => {
    if (!isProd) return true;
    
    if (isEditing) {
      const editedUpdate = scheduledUpdates.find(update => update.id === editingId);
      if (editedUpdate && isSameDay(new Date(editedUpdate.date), date) && 
          editedUpdate.environment === 'production' &&
          (editedUpdate.components.patientApp || 
           editedUpdate.components.managerFrontApp || 
           editedUpdate.components.marshaller || 
           editedUpdate.components.marshallerInterop)) {
        setFormErrors(prev => ({ ...prev, prodLimit: null }));
        return true;
      }
    }
    
    const prodUpdatesForDate = scheduledUpdates.filter(update => 
      isSameDay(new Date(update.date), date) && 
      update.environment === 'production' &&
      (update.components.patientApp || 
       update.components.managerFrontApp || 
       update.components.marshaller || 
       update.components.marshallerInterop) &&
      (editingId === null || update.id !== editingId)
    );
    
    if (prodUpdatesForDate.length > 0) {
      setFormErrors(prev => ({
        ...prev,
        prodLimit: 'Une mise à jour en production est déjà planifiée pour cette date'
      }));
      return false;
    } else {
      setFormErrors(prev => ({
        ...prev,
        prodLimit: null
      }));
      return true;
    }
  };

  const handleTimeSlotChange = (e) => {
    const newTimeSlot = e.target.value;
    setSelectedTimeSlot(newTimeSlot);
    validateTimeSlot(selectedDate, newTimeSlot);
  };

  const handleComponentChange = (e) => {
    const { name, checked } = e.target;
    setSelectedComponents(prev => ({
      ...prev,
      [name]: checked
    }));
    
    const isProdComponent = ['patientApp', 'managerFrontApp', 'marshaller', 'marshallerInterop'].includes(name);
    if (isProdComponent && checked && selectedEnvironment === 'production') {
      validateProdLimit(selectedDate, true);
    }
    
    if (!checked) {
      setTargetVersions(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleVersionChange = (component, version) => {
    setTargetVersions(prev => ({
      ...prev,
      [component]: version
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const hasSelectedComponent = Object.values(selectedComponents).some(value => value);
    if (!hasSelectedComponent) {
      setFormErrors(prev => ({
        ...prev,
        components: 'Veuillez sélectionner au moins un composant à mettre à jour'
      }));
      return;
    }
    
    if (!selectedClient) {
      setFormErrors(prev => ({
        ...prev,
        client: 'Veuillez sélectionner un client'
      }));
      return;
    }
    
    if (!jiraTicket) {
      setFormErrors(prev => ({
        ...prev,
        jiraTicket: 'Veuillez saisir un numéro de ticket JIRA ou créer un nouveau ticket'
      }));
      return;
    }
    
    const isProdUpdate = selectedEnvironment === 'production' && (
      selectedComponents.patientApp || 
      selectedComponents.managerFrontApp || 
      selectedComponents.marshaller || 
      selectedComponents.marshallerInterop
    );
    
    if (isProdUpdate) {
      const prodLimitValid = validateProdLimit(selectedDate, true);
      if (!prodLimitValid) return;
    }
    
    const selectedClientObj = clients.find(client => client.id === selectedClient);
    const updateData = {
      clientId: selectedClient,
      clientName: selectedClientObj ? selectedClientObj.name : `Client ${selectedClient}`,
      environment: selectedEnvironment,
      date: selectedDate.toISOString(),
      timeSlot: selectedTimeSlot,
      jiraTicket,
      description: updateDescription,
      components: { ...selectedComponents },
      targetVersions: { ...targetVersions },
      owner: 'Utilisateur actuel',
      status: 'planned'
    };
    
    try {
      if (isEditing) {
        const response = await fetch(`/api/scheduled-updates/${editingId}`, {
          method: 'PUT',
          headers: bastionAuth.getAuthHeaders(),
          body: JSON.stringify(updateData)
        });
        
        if (response.ok) {
          const updatedData = await response.json();
          setScheduledUpdates(prev => prev.map(update => 
            update.id === editingId ? { ...updatedData, date: new Date(updatedData.date) } : update
          ));
        }
      } else {
        const response = await fetch('/api/scheduled-updates', {
          method: 'POST',
          headers: bastionAuth.getAuthHeaders(),
          body: JSON.stringify(updateData)
        });
        
        if (response.ok) {
          const newUpdate = await response.json();
          setScheduledUpdates(prev => [...prev, { ...newUpdate, date: new Date(newUpdate.date) }]);
        }
      }
      
      // Réinitialiser le formulaire
      setFormVisible(false);
      setFormErrors({});
      setJiraCreationResult(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // Génère un calendrier pour le mois sélectionné
  const generateCalendar = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const firstDayIndex = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    for (let i = 0; i < firstDayIndex; i++) {
      days.push({ day: '', isCurrentMonth: false });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isToday = isSameDay(date, new Date());
      const isSelected = isSameDay(date, selectedDate);
      
      const updates = scheduledUpdates.filter(update => isSameDay(new Date(update.date), date));
      
      const hasProdUpdate = updates.some(update => 
        update.environment === 'production' &&
        update.components && (
          update.components.patientApp || 
          update.components.managerFrontApp || 
          update.components.marshaller || 
          update.components.marshallerInterop
        )
      );
      
      const hasQualUpdate = updates.some(update => update.environment === 'qualification');
      
      days.push({
        day: i,
        isCurrentMonth: true,
        isToday,
        isSelected,
        updates,
        hasProdUpdate,
        hasQualUpdate
      });
    }
    
    return days;
  };

  const isSameDay = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const handlePreviousMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    if (day.isCurrentMonth) {
      const newDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day.day);
      showForm(newDate);
    }
  };

  const weekdays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'planned': return 'Planifiée';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminée';
      case 'cancelled': return 'Annulée';
      case 'failed': return 'Échouée';
      default: return status;
    }
  };

  // Filtrer les mises à jour selon le statut
  const filteredUpdates = scheduledUpdates.filter(update => {
    if (updateStatusFilter === 'all') return true;
    return update.status === updateStatusFilter;
  });

  if (loading) {
    return <div className="text-center py-10">Chargement...</div>;
  }

  // Debug: Vérifier l'état des organisations avant le rendu
  console.log('🎯 État final des organisations:', {
    organizationsLoaded,
    jiraOrganizations,
    count: jiraOrganizations.length,
    selectedOrganization
  });
  

  const calendar = generateCalendar();

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Planification des mises à jour</h1>
      
      {/* Calendrier */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <button onClick={handlePreviousMonth} className="p-2 rounded-full hover:bg-gray-200">
            <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-xl font-semibold">
            {months[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
          </h2>
          <button onClick={handleNextMonth} className="p-2 rounded-full hover:bg-gray-200">
            <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {weekdays.map(day => (
            <div key={day} className="text-center font-medium text-gray-600 py-2">
              {day}
            </div>
          ))}
          
          {calendar.map((day, index) => (
            <div 
              key={index} 
              className={`p-1 h-20 ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-100'} border rounded-md relative cursor-pointer hover:bg-gray-50`}
              onClick={() => day.isCurrentMonth && handleDateClick(day)}
            >
              {day.day && (
                <>
                  <div 
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      day.isSelected ? 'bg-purple-600 text-white' : 
                      day.isToday ? 'bg-blue-100 text-blue-800' : ''
                    }`}
                  >
                    {day.day}
                  </div>
                  
                  <div className="absolute top-1 right-1 flex gap-1">
                    {day.hasProdUpdate && (
                      <div className="w-3 h-3 bg-red-500 rounded-full" title="Mise à jour en production"></div>
                    )}
                    {day.hasQualUpdate && (
                      <div className="w-3 h-3 bg-orange-500 rounded-full" title="Mise à jour en qualification"></div>
                    )}
                  </div>
                  
                  {day.updates.length > 0 && (
                    <div className="mt-1 text-xs">
                      {day.updates.slice(0, 2).map((update, i) => (
                        <div key={i} className="truncate text-xs p-0.5 mb-0.5 bg-blue-50 rounded border border-blue-100">
                          {update.clientName.substring(0, 10)}{update.clientName.length > 10 ? '...' : ''}
                        </div>
                      ))}
                      {day.updates.length > 2 && (
                        <div className="text-xs text-gray-500 text-center">+{day.updates.length - 2} autres</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-4 flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Production</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span>Qualification</span>
          </div>
        </div>
      </div>
      
      {/* Filtre par statut */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center gap-4">
          <span className="font-medium">Filtrer par statut :</span>
          <select
            value={updateStatusFilter}
            onChange={(e) => setUpdateStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="all">Toutes</option>
            <option value="planned">Planifiées</option>
            <option value="in_progress">En cours</option>
            <option value="completed">Terminées</option>
            <option value="cancelled">Annulées</option>
            <option value="failed">Échouées</option>
          </select>
        </div>
      </div>
      
      {/* Liste des mises à jour planifiées */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <h2 className="text-xl font-semibold mb-4">Mises à jour planifiées</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Heure</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Environnement</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket JIRA</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Composants & Versions</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsable</th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUpdates.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-3 py-4 text-center text-gray-500">
                    Aucune mise à jour trouvée
                  </td>
                </tr>
              ) : (
                filteredUpdates.sort((a, b) => new Date(a.date) - new Date(b.date)).map(update => (
                  <tr key={update.id}>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(update.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {update.timeSlot}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {update.clientName}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        update.environment === 'production' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {update.environment === 'production' ? 'PROD' : 'QUAL'}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-blue-600">
                      <a href={`https://hoppen.atlassian.net/browse/${update.jiraTicket}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {update.jiraTicket}
                      </a>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {update.description || '-'}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      <div className="space-y-1">
                        {update.components && update.components.patientApp && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">Patient App</span>
                            {update.targetVersions?.patientApp && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.patientApp}</span>
                            )}
                          </div>
                        )}
                        {update.components && update.components.managerFrontApp && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">Manager</span>
                            {update.targetVersions?.managerFrontApp && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.managerFrontApp}</span>
                            )}
                          </div>
                        )}
                        {update.components && update.components.marshaller && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">Marshaller</span>
                            {update.targetVersions?.marshaller && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.marshaller}</span>
                            )}
                          </div>
                        )}
                        {update.components && update.components.marshallerInterop && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">M-Interop</span>
                            {update.targetVersions?.marshallerInterop && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.marshallerInterop}</span>
                            )}
                          </div>
                        )}
                        {update.components && update.components.apache && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">Apache</span>
                            {update.targetVersions?.apache && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.apache}</span>
                            )}
                          </div>
                        )}
                        {update.components && update.components.mariadb && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">MariaDB</span>
                            {update.targetVersions?.mariadb && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.mariadb}</span>
                            )}
                          </div>
                        )}
                        {update.components && update.components.nodejs && (
                          <div className="flex items-center gap-1">
                            <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">Node.js</span>
                            {update.targetVersions?.nodejs && (
                              <span className="text-xs text-gray-600">→ {update.targetVersions.nodejs}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm">
                      <select
                        value={update.status || 'planned'}
                        onChange={(e) => changeUpdateStatus(update.id, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs ${getStatusColor(update.status || 'planned')}`}
                      >
                        <option value="planned">Planifiée</option>
                        <option value="in_progress">En cours</option>
                        <option value="completed">Terminée</option>
                        <option value="cancelled">Annulée</option>
                        <option value="failed">Échouée</option>
                      </select>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {update.owner}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-500">
                      <button 
                        className="hoppen-btn px-2 py-1 rounded text-xs mr-2"
                        onClick={() => editUpdate(update.id)}
                      >
                        Modifier
                      </button>
                      <button 
                        className="bg-red-600 text-white px-2 py-1 rounded text-xs"
                        onClick={() => deleteUpdate(update.id)}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Formulaire de planification de mise à jour */}
      {formVisible && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {isEditing ? 'Modifier une mise à jour' : 'Planifier une mise à jour'}
          </h2>
          <h3 className="text-md font-medium mb-2 text-gray-700">
            Date sélectionnée: {selectedDate.toLocaleDateString('fr-FR')}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="">Sélectionnez un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
                {formErrors.client && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.client}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Environnement *</label>
                <select
                  value={selectedEnvironment}
                  onChange={(e) => setSelectedEnvironment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="production">Production</option>
                  <option value="qualification">Qualification</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organisation JIRA *</label>
                <select
                  value={selectedOrganization}
                  onChange={(e) => setSelectedOrganization(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  {jiraOrganizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name || `Organisation ${org.id}`}
                    </option>
                  ))}
                </select>
                {formErrors.organization && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.organization}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Créneau horaire *</label>
                <select
                  value={selectedTimeSlot}
                  onChange={handleTimeSlotChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="08:00-09:00">08:00 - 09:00</option>
                  <option value="09:00-10:00">09:00 - 10:00</option>
                  <option value="10:00-11:00">10:00 - 11:00</option>
                  <option value="11:00-12:00">11:00 - 12:00</option>
                  <option value="12:00-13:00">12:00 - 13:00</option>
                  <option value="13:00-14:00">13:00 - 14:00</option>
                  <option value="14:00-15:00">14:00 - 15:00</option>
                  <option value="15:00-16:00">15:00 - 16:00</option>
                  <option value="16:00-17:00">16:00 - 17:00</option>
                </select>
                {formErrors.timeSlot && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.timeSlot}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket JIRA *</label>
                <div className="space-y-2">
                  {jiraIssueTypes.length > 0 && (
                    <select
                      value={selectedIssueType}
                      onChange={(e) => setSelectedIssueType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    >
                      {jiraIssueTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name} {type.description ? `- ${type.description}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={jiraTicket}
                      onChange={(e) => setJiraTicket(e.target.value)}
                      placeholder="Ex: ENGSUP-1234"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={createJiraTicket}
                      disabled={isCreatingJiraTicket || !selectedClient}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isCreatingJiraTicket || !selectedClient
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isCreatingJiraTicket ? 'Création...' : 'Créer ticket'}
                    </button>
                  </div>
                </div>
                {formErrors.jiraTicket && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.jiraTicket}</p>
                )}
                {jiraCreationResult && (
                  <div className={`mt-2 p-2 rounded-md text-sm ${
                    jiraCreationResult.success 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {jiraCreationResult.success ? (
                      <div>
                        <div className="font-medium">✅ {jiraCreationResult.message}</div>
                        <div className="mt-1">
                          <a 
                            href={jiraCreationResult.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="underline"
                          >
                            Voir le ticket
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div>❌ {jiraCreationResult.error}</div>
                    )}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={updateDescription}
                  onChange={(e) => setUpdateDescription(e.target.value)}
                  placeholder="Brève description de la mise à jour"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Version globale (optionnel)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={globalVersion}
                  onChange={(e) => setGlobalVersion(e.target.value)}
                  placeholder="Ex: 7.41.10"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={applyGlobalVersion}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Appliquer à tous
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Cette version sera appliquée à tous les composants sélectionnés
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Composants à mettre à jour et versions cibles *</label>
              {formErrors.prodLimit && (
                <p className="mb-2 text-sm text-red-600 font-medium">{formErrors.prodLimit}</p>
              )}
              {formErrors.components && (
                <p className="mb-2 text-sm text-red-600">{formErrors.components}</p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-purple-700">Composants en production</h4>
                  <p className="text-xs text-gray-500 italic">Limité à 1 mise à jour en production par jour</p>
                  
                  <div className="space-y-2">
                    {['patientApp', 'managerFrontApp', 'marshaller', 'marshallerInterop'].map(component => (
                      <div key={component} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={component}
                            name={component}
                            checked={selectedComponents[component]}
                            onChange={handleComponentChange}
                            className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                          />
                          <label htmlFor={component} className="ml-2 text-sm text-gray-700">
                            {component === 'patientApp' ? 'Patient App' :
                             component === 'managerFrontApp' ? 'Manager Front App' :
                             component === 'marshaller' ? 'Marshaller' :
                             'Marshaller Interop'}
                          </label>
                        </div>
                        {selectedComponents[component] && (
                          <input
                            type="text"
                            value={targetVersions[component]}
                            onChange={(e) => handleVersionChange(component, e.target.value)}
                            placeholder="Version cible"
                            className="ml-2 px-2 py-1 text-xs border border-gray-300 rounded-md w-24"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-blue-700">Infrastructures</h4>
                  <p className="text-xs text-gray-500 italic">Pas de limite quotidienne</p>
                  
                  <div className="space-y-2">
                    {['apache', 'mariadb', 'nodejs'].map(component => (
                      <div key={component} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={component}
                            name={component}
                            checked={selectedComponents[component]}
                            onChange={handleComponentChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                          <label htmlFor={component} className="ml-2 text-sm text-gray-700">
                            {component === 'apache' ? 'Apache' :
                             component === 'mariadb' ? 'MariaDB' :
                             'Node.js'}
                          </label>
                        </div>
                        {selectedComponents[component] && (
                          <input
                            type="text"
                            value={targetVersions[component]}
                            onChange={(e) => handleVersionChange(component, e.target.value)}
                            placeholder="Version cible"
                            className="ml-2 px-2 py-1 text-xs border border-gray-300 rounded-md w-24"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setFormVisible(false);
                  setJiraCreationResult(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="hoppen-btn px-4 py-2 rounded shadow hover:shadow-md transition-all"
              >
                {isEditing ? 'Enregistrer les modifications' : 'Planifier la mise à jour'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {!formVisible && (
        <div className="flex justify-center">
          <button
            onClick={() => showForm(new Date())}
            className="hoppen-btn px-4 py-2 rounded shadow hover:shadow-md transition-all"
          >
            + Planifier une nouvelle mise à jour
          </button>
        </div>
      )}
    </div>
  );
};

export default UpdatePlanning;
